'use client';

import React, { useState, useEffect, useCallback } from 'react';
import Link from 'next/link';
import { createClient } from '@/lib/supabase/client';

// ─── PUBLIC GARDEN GALLERY ────────────────────────────────────────────────────
// Searchable gallery of public gardens and branches with usage stats,
// embedded instances, fork/adapt buttons, and premade starter gardens.

interface PublicGarden {
  id: string;
  slug: string;
  title: string;
  description: string;
  lineage: string;
  vocation: string;
  is_public: boolean;
  fork_count?: number;
  view_count?: number;
  created_at: string;
  updated_at: string;
  modules?: { module_type: string; title: string; icon?: string }[];
}

// ─── PREMADE STARTER GARDENS ─────────────────────────────────────────────────

interface StarterGarden {
  id: string;
  title: string;
  arabic: string;
  description: string;
  vocation: string;
  lineage: string;
  icon: string;
  color: string;
  tags: string[];
  modules: { type: string; title: string; icon: string; note: string }[];
  seedVerse: string;
  seedVerseRef: string;
}

const STARTER_GARDENS: StarterGarden[] = [
  {
    id: 'starter-irl-observer',
    title: 'The Observer\'s Garden',
    arabic: 'حَدِيقَةُ الْمُرَاقِب',
    description: 'Begin with the IRL Lens and Names Reference. A minimal garden for caretakers who want to start reading signs in creation immediately.',
    vocation: 'Observer',
    lineage: 'Forest Keeper',
    icon: '🔬',
    color: 'rgba(45,138,135,0.8)',
    tags: ['irl-lens', 'names', 'observation', 'beginner'],
    modules: [
      { type: 'irl_lens', title: 'IRL Lens', icon: '🔬', note: 'The 8-layer recognition pathway — start here' },
      { type: 'names_tile', title: 'Names Reference', icon: '✦', note: 'Open the root when you extract a Name' },
      { type: 'concept_term', title: 'Ayat in Creation', icon: '🌿', note: 'Signs in the horizons and in yourselves' },
    ],
    seedVerse: 'We will show them Our signs in the horizons and within themselves until it becomes clear to them that it is the truth.',
    seedVerseRef: 'Quran 41:53',
  },
  {
    id: 'starter-community-caretaker',
    title: 'The Community Caretaker',
    arabic: 'حَدِيقَةُ الْخَادِم',
    description: 'MRG + IRL Lens + Quests. For caretakers whose work is in the ward — mapping resources, identifying gaps, building bridges.',
    vocation: 'Community Organizer',
    lineage: 'Water Guardian',
    icon: '⬡',
    color: 'rgba(45,122,71,0.8)',
    tags: ['mrg', 'community', 'service', 'ward'],
    modules: [
      { type: 'mrg', title: 'Muslim Resource Guide', icon: '🗺', note: 'Your ward as a field of Ayat' },
      { type: 'irl_lens', title: 'IRL Lens', icon: '🔬', note: 'Apply the Three Questions to community encounters' },
      { type: 'quest', title: 'Quests', icon: '⬡', note: 'Ward Amanah and Fisabilillah Khidmah quests' },
      { type: 'concept_term', title: 'Community', icon: '🌐', note: 'Nations and tribes that you may know one another' },
    ],
    seedVerse: 'O Mankind, indeed We have created you from a male and a female and made you peoples and tribes that you may know one another.',
    seedVerseRef: 'Quran 49:13',
  },
  {
    id: 'starter-scholar',
    title: 'The Scholar\'s Garden',
    arabic: 'حَدِيقَةُ الْعَالِم',
    description: 'Keys + Names + SL Glossary + Open Letter. For caretakers who want to go deep into the theological and linguistic roots of the framework.',
    vocation: 'Academic / Student',
    lineage: 'Edge Walker',
    icon: '📖',
    color: 'rgba(201,168,76,0.8)',
    tags: ['keys', 'names', 'glossary', 'theology', 'roots'],
    modules: [
      { type: 'keys_card', title: 'Keys to Kulli Haal', icon: '🗝', note: 'The 7-key curriculum — Ayat, Haal, Taqwah, Mizan, Amanah, Haqq, Islah' },
      { type: 'names_tile', title: 'Names Reference', icon: '✦', note: 'Root meaning fields — not single definitions' },
      { type: 'concept_term', title: 'SL Glossary', icon: '📖', note: 'Etymology and meaning constellations' },
      { type: 'letter_section', title: 'Open Letter', icon: '📜', note: 'The foundational text — the garden\'s soil' },
    ],
    seedVerse: 'Whoever takes a path upon which to obtain knowledge, Allah makes the path to Paradise easy for him.',
    seedVerseRef: 'Tirmidhi 2646',
  },
  {
    id: 'starter-restoration',
    title: 'The Restoration Garden',
    arabic: 'حَدِيقَةُ الْإِصْلَاح',
    description: 'IRL Lens + MRG + Bridge modules. For caretakers focused on Islah — reading the conditions of the community and bridging toward restoration.',
    vocation: 'Activist / Organizer',
    lineage: 'Restoration Warrior',
    icon: '🌱',
    color: 'rgba(45,122,71,0.6)',
    tags: ['islah', 'restoration', 'bridge', 'community'],
    modules: [
      { type: 'irl_lens', title: 'IRL Lens', icon: '🔬', note: 'Fission sequence applied to community barriers' },
      { type: 'mrg', title: 'Muslim Resource Guide', icon: '🗺', note: 'Map where Islah is most needed' },
      { type: 'concept_term', title: 'Bridge: Environment Design', icon: '🌉', note: 'The designed environment as a reading of the Qadr' },
      { type: 'concept_term', title: 'Ayat in Creation', icon: '🌿', note: 'Signs of restoration already present' },
    ],
    seedVerse: 'And do not cause corruption upon the earth after its reformation.',
    seedVerseRef: 'Quran 7:56',
  },
  {
    id: 'starter-vocational',
    title: 'The Vocational Lens',
    arabic: 'حَدِيقَةُ الْمِهْنَة',
    description: 'Vocation-first garden. Begin from your work — engineer, teacher, farmer, janitor, law student — and apply the IRL Lens through your specific role.',
    vocation: 'Any Vocation',
    lineage: 'Any Lineage',
    icon: '⌘',
    color: 'rgba(139,105,20,0.8)',
    tags: ['vocation', 'role', 'khalifah', 'work'],
    modules: [
      { type: 'irl_lens', title: 'IRL Lens', icon: '🔬', note: 'The same methodology, filtered through your specific work' },
      { type: 'keys_card', title: 'Keys to Kulli Haal', icon: '🗝', note: 'What does Amanah look like in your vocation?' },
      { type: 'concept_term', title: 'Community', icon: '🌐', note: 'Your vocation as a node in the mycorrhizal network' },
    ],
    seedVerse: 'What tools is an engineer working on, and what tools does this engineer utilize toward that purpose? Fisabilillah.',
    seedVerseRef: 'Open Letter — Vocational Passage',
  },
  {
    id: 'starter-names-deep',
    title: 'The Names Garden',
    arabic: 'حَدِيقَةُ الْأَسْمَاء',
    description: 'Names Reference + IRL Lens + SL Glossary. A garden for caretakers who want to practice Root Fission — cracking open the three-letter roots of the Names.',
    vocation: 'Names Seeker',
    lineage: 'Forest Keeper',
    icon: '✦',
    color: 'rgba(138,138,184,0.8)',
    tags: ['names', 'roots', 'fission', 'theology'],
    modules: [
      { type: 'names_tile', title: 'Names Reference', icon: '✦', note: 'He taught Adam the names — all of them (Quran 2:31)' },
      { type: 'irl_lens', title: 'IRL Lens', icon: '🔬', note: 'Root Fission: after extracting a Name, crack open its root' },
      { type: 'concept_term', title: 'SL Glossary', icon: '📖', note: 'Meaning fields — constellations of interrelated senses' },
      { type: 'letter_section', title: 'Open Letter', icon: '📜', note: 'Al-Qadir: twelve dimensions of ق-د-ر' },
    ],
    seedVerse: 'And He taught Adam the names of all things.',
    seedVerseRef: 'Quran 2:31',
  },
];

// ─── MODULE ICON MAP ──────────────────────────────────────────────────────────

const MODULE_ICONS: Record<string, string> = {
  irl_lens: '🔬', mrg: '🗺', names_tile: '✦', keys_card: '🗝',
  letter_section: '📜', concept_term: '◈', quest: '⬡',
};

// ─── EMBED PREVIEW ────────────────────────────────────────────────────────────

function EmbedPreview({ slug, title }: { slug: string; title: string }) {
  const [copied, setCopied] = useState(false);
  const url = `https://haalproject.org/garden/${slug}`;
  const iframe = `<iframe src="${url}" width="100%" height="600" frameborder="0" style="border-radius:8px;border:1px solid rgba(201,168,76,0.2);" title="${title}"></iframe>`;

  const copy = () => {
    navigator.clipboard.writeText(iframe).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    });
  };

  return (
    <div style={{ marginTop: 10, padding: '10px 12px', background: 'rgba(45,138,135,0.04)', border: '1px solid rgba(45,138,135,0.15)', borderRadius: 6 }}>
      <p style={{ fontFamily: 'monospace', fontSize: 8, letterSpacing: '.1em', textTransform: 'uppercase', color: 'rgba(45,138,135,0.6)', marginBottom: 6 }}>Embed Code</p>
      <code style={{ display: 'block', fontFamily: 'monospace', fontSize: 9, color: 'rgba(212,197,169,0.5)', lineHeight: 1.5, wordBreak: 'break-all', marginBottom: 8 }}>
        {iframe.slice(0, 80)}…
      </code>
      <button
        onClick={copy}
        style={{ fontFamily: 'monospace', fontSize: 8, letterSpacing: '.1em', textTransform: 'uppercase', padding: '4px 10px', border: '1px solid rgba(45,138,135,0.3)', background: 'transparent', color: copied ? 'rgba(45,200,100,0.8)' : 'rgba(45,138,135,0.7)', borderRadius: 4, cursor: 'pointer' }}
      >
        {copied ? '✓ Copied' : '⬡ Copy Embed'}
      </button>
    </div>
  );
}

// ─── STARTER GARDEN CARD ─────────────────────────────────────────────────────

function StarterGardenCard({ garden, onFork }: { garden: StarterGarden; onFork: (g: StarterGarden) => void }) {
  const [expanded, setExpanded] = useState(false);

  return (
    <div style={{
      background: 'rgba(255,255,255,0.02)',
      border: `1px solid ${garden.color.replace('0.8', '0.25')}`,
      borderLeft: `3px solid ${garden.color}`,
      borderRadius: 10,
      padding: '16px 18px',
      transition: 'border-color 0.2s',
    }}>
      <div style={{ display: 'flex', alignItems: 'flex-start', gap: 12, marginBottom: 10 }}>
        <div style={{
          width: 40, height: 40, borderRadius: 8, flexShrink: 0,
          background: garden.color.replace('0.8', '0.1'),
          border: `1px solid ${garden.color.replace('0.8', '0.3')}`,
          display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 18,
        }}>
          {garden.icon}
        </div>
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap', marginBottom: 2 }}>
            <h3 style={{ fontFamily: 'monospace', fontSize: 13, fontWeight: 700, color: '#F5F0E8', margin: 0 }}>{garden.title}</h3>
            <span style={{ fontFamily: "'Amiri', serif", fontSize: 13, color: garden.color, direction: 'rtl' }}>{garden.arabic}</span>
          </div>
          <p style={{ fontFamily: 'monospace', fontSize: 9, color: 'rgba(212,197,169,0.4)', marginBottom: 0 }}>
            {garden.vocation} · {garden.lineage}
          </p>
        </div>
      </div>

      <p style={{ fontFamily: 'monospace', fontSize: 11, color: 'rgba(212,197,169,0.6)', lineHeight: 1.65, marginBottom: 10 }}>
        {garden.description}
      </p>

      {/* Seed verse */}
      <div style={{ background: 'rgba(201,168,76,0.04)', border: '1px solid rgba(201,168,76,0.1)', borderRadius: 6, padding: '8px 12px', marginBottom: 10 }}>
        <p style={{ fontFamily: "'Georgia', serif", fontStyle: 'italic', fontSize: 11, color: 'rgba(212,197,169,0.65)', lineHeight: 1.6, marginBottom: 4 }}>
          "{garden.seedVerse}"
        </p>
        <p style={{ fontFamily: 'monospace', fontSize: 8, color: 'rgba(201,168,76,0.5)', letterSpacing: '.08em' }}>{garden.seedVerseRef}</p>
      </div>

      {/* Module list */}
      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 5, marginBottom: 12 }}>
        {garden.modules.map((m, i) => (
          <span key={i} style={{
            fontFamily: 'monospace', fontSize: 9, padding: '3px 8px',
            background: 'rgba(255,255,255,0.03)', border: '1px solid rgba(255,255,255,0.07)',
            borderRadius: 4, color: 'rgba(212,197,169,0.5)',
          }}>
            {m.icon} {m.title}
          </span>
        ))}
      </div>

      {/* Expanded module notes */}
      {expanded && (
        <div style={{ marginBottom: 12, padding: '10px 12px', background: 'rgba(255,255,255,0.015)', borderRadius: 6, border: '1px solid rgba(255,255,255,0.05)' }}>
          {garden.modules.map((m, i) => (
            <div key={i} style={{ display: 'flex', gap: 8, marginBottom: 6, alignItems: 'flex-start' }}>
              <span style={{ fontSize: 12, flexShrink: 0 }}>{m.icon}</span>
              <div>
                <span style={{ fontFamily: 'monospace', fontSize: 10, color: 'rgba(212,197,169,0.7)', fontWeight: 700 }}>{m.title}</span>
                <span style={{ fontFamily: 'monospace', fontSize: 10, color: 'rgba(212,197,169,0.4)' }}> — {m.note}</span>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Tags */}
      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 4, marginBottom: 12 }}>
        {garden.tags.map(tag => (
          <span key={tag} style={{ fontFamily: 'monospace', fontSize: 8, padding: '2px 6px', background: 'rgba(201,168,76,0.05)', border: '1px solid rgba(201,168,76,0.12)', borderRadius: 3, color: 'rgba(201,168,76,0.5)', letterSpacing: '.06em' }}>
            {tag}
          </span>
        ))}
      </div>

      {/* Actions */}
      <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
        <button
          onClick={() => onFork(garden)}
          style={{
            fontFamily: 'monospace', fontSize: 9, letterSpacing: '.1em', textTransform: 'uppercase',
            padding: '6px 14px', border: '1px solid rgba(45,200,100,0.35)',
            background: 'rgba(45,122,71,0.08)', color: 'rgba(45,200,100,0.85)',
            borderRadius: 5, cursor: 'pointer', transition: 'all 0.15s',
          }}
        >
          🌱 Begin from this Garden
        </button>
        <button
          onClick={() => setExpanded(v => !v)}
          style={{
            fontFamily: 'monospace', fontSize: 9, letterSpacing: '.1em', textTransform: 'uppercase',
            padding: '6px 12px', border: '1px solid rgba(255,255,255,0.07)',
            background: 'transparent', color: 'rgba(212,197,169,0.4)',
            borderRadius: 5, cursor: 'pointer',
          }}
        >
          {expanded ? '▲ Less' : '▼ Module Notes'}
        </button>
      </div>
    </div>
  );
}

// ─── PUBLIC GARDEN CARD ───────────────────────────────────────────────────────

function PublicGardenCard({ garden, onFork }: { garden: PublicGarden; onFork: (g: PublicGarden) => void }) {
  const [showEmbed, setShowEmbed] = useState(false);

  const moduleIcons = (garden.modules || []).slice(0, 5).map(m => MODULE_ICONS[m.module_type] || '◈');

  return (
    <div style={{
      background: 'rgba(255,255,255,0.02)',
      border: '1px solid rgba(255,255,255,0.07)',
      borderRadius: 10,
      padding: '16px 18px',
      transition: 'border-color 0.2s',
    }}>
      <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 10, marginBottom: 8 }}>
        <div style={{ flex: 1, minWidth: 0 }}>
          <h3 style={{ fontFamily: 'monospace', fontSize: 13, fontWeight: 700, color: '#F5F0E8', marginBottom: 3 }}>{garden.title}</h3>
          {garden.vocation && (
            <p style={{ fontFamily: 'monospace', fontSize: 9, color: 'rgba(201,168,76,0.5)', marginBottom: 0 }}>{garden.vocation}</p>
          )}
        </div>
        <div style={{ display: 'flex', gap: 10, flexShrink: 0 }}>
          {garden.view_count !== undefined && (
            <div style={{ textAlign: 'center' }}>
              <p style={{ fontFamily: 'monospace', fontSize: 13, fontWeight: 700, color: '#c9a84c', margin: 0 }}>{garden.view_count}</p>
              <p style={{ fontFamily: 'monospace', fontSize: 7, color: 'rgba(212,197,169,0.3)', letterSpacing: '.08em', textTransform: 'uppercase' }}>views</p>
            </div>
          )}
          {garden.fork_count !== undefined && (
            <div style={{ textAlign: 'center' }}>
              <p style={{ fontFamily: 'monospace', fontSize: 13, fontWeight: 700, color: 'rgba(45,200,100,0.8)', margin: 0 }}>{garden.fork_count}</p>
              <p style={{ fontFamily: 'monospace', fontSize: 7, color: 'rgba(212,197,169,0.3)', letterSpacing: '.08em', textTransform: 'uppercase' }}>forks</p>
            </div>
          )}
        </div>
      </div>

      {garden.description && (
        <p style={{ fontFamily: 'monospace', fontSize: 11, color: 'rgba(212,197,169,0.5)', lineHeight: 1.6, marginBottom: 10 }}>
          {garden.description.slice(0, 120)}{garden.description.length > 120 ? '…' : ''}
        </p>
      )}

      {/* Module icons */}
      {moduleIcons.length > 0 && (
        <div style={{ display: 'flex', gap: 4, marginBottom: 12 }}>
          {moduleIcons.map((icon, i) => (
            <span key={i} style={{ fontSize: 14, opacity: 0.7 }}>{icon}</span>
          ))}
          {(garden.modules || []).length > 5 && (
            <span style={{ fontFamily: 'monospace', fontSize: 9, color: 'rgba(212,197,169,0.3)', alignSelf: 'center' }}>+{(garden.modules || []).length - 5}</span>
          )}
        </div>
      )}

      {/* Actions */}
      <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
        <Link
          href={`/garden/${garden.slug}`}
          style={{
            fontFamily: 'monospace', fontSize: 9, letterSpacing: '.1em', textTransform: 'uppercase',
            padding: '5px 12px', border: '1px solid rgba(45,138,135,0.3)',
            background: 'rgba(45,138,135,0.06)', color: 'rgba(45,200,150,0.8)',
            borderRadius: 5, textDecoration: 'none', display: 'inline-block',
          }}
        >
          ↗ Visit
        </Link>
        <button
          onClick={() => onFork(garden)}
          style={{
            fontFamily: 'monospace', fontSize: 9, letterSpacing: '.1em', textTransform: 'uppercase',
            padding: '5px 12px', border: '1px solid rgba(45,200,100,0.25)',
            background: 'rgba(45,122,71,0.06)', color: 'rgba(45,200,100,0.7)',
            borderRadius: 5, cursor: 'pointer',
          }}
        >
          🌿 Fork / Adapt
        </button>
        <button
          onClick={() => setShowEmbed(v => !v)}
          style={{
            fontFamily: 'monospace', fontSize: 9, letterSpacing: '.1em', textTransform: 'uppercase',
            padding: '5px 12px', border: '1px solid rgba(255,255,255,0.07)',
            background: 'transparent', color: 'rgba(212,197,169,0.4)',
            borderRadius: 5, cursor: 'pointer',
          }}
        >
          {'<>'} Embed
        </button>
      </div>

      {showEmbed && <EmbedPreview slug={garden.slug} title={garden.title} />}
    </div>
  );
}

// ─── FORK CONFIRMATION ────────────────────────────────────────────────────────

function ForkConfirmation({ garden, onClose }: { garden: StarterGarden | PublicGarden | null; onClose: () => void }) {
  if (!garden) return null;
  const title = 'title' in garden ? garden.title : garden.title;
  return (
    <div style={{
      position: 'fixed', inset: 0, background: 'rgba(6,13,18,0.92)', zIndex: 200,
      display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 24,
    }}>
      <div style={{ maxWidth: 440, width: '100%', background: 'rgba(10,20,28,0.99)', border: '1px solid rgba(45,200,100,0.25)', borderRadius: 12, padding: '28px 28px 24px' }}>
        <p style={{ fontFamily: 'monospace', fontSize: 8, letterSpacing: '.15em', textTransform: 'uppercase', color: 'rgba(45,200,100,0.6)', marginBottom: 10 }}>🌱 Fork / Adapt Garden</p>
        <h3 style={{ fontFamily: 'monospace', fontSize: 16, fontWeight: 700, color: '#F5F0E8', marginBottom: 8 }}>{title}</h3>
        <p style={{ fontFamily: 'monospace', fontSize: 11, color: 'rgba(212,197,169,0.55)', lineHeight: 1.7, marginBottom: 20 }}>
          This garden will be copied to your Garden Workshop where you can edit, re-arrange, add or remove modules, and cultivate it further. Your fork is your own — the original remains unchanged.
        </p>
        <div style={{ background: 'rgba(201,168,76,0.04)', border: '1px solid rgba(201,168,76,0.12)', borderRadius: 6, padding: '10px 14px', marginBottom: 20 }}>
          <p style={{ fontFamily: "'Georgia', serif", fontStyle: 'italic', fontSize: 11, color: 'rgba(212,197,169,0.6)', lineHeight: 1.6 }}>
            "The garden is a reading tool for a text that was written before time began. Your work is not to write — but to read."
          </p>
        </div>
        <div style={{ display: 'flex', gap: 8 }}>
          <Link
            href="/user-dashboard"
            onClick={onClose}
            style={{
              flex: 1, fontFamily: 'monospace', fontSize: 9, letterSpacing: '.1em', textTransform: 'uppercase',
              padding: '10px 16px', border: '1px solid rgba(45,200,100,0.35)',
              background: 'rgba(45,122,71,0.1)', color: 'rgba(45,200,100,0.9)',
              borderRadius: 6, textDecoration: 'none', textAlign: 'center', display: 'block',
            }}
          >
            🌱 Go to My Garden
          </Link>
          <button
            onClick={onClose}
            style={{
              fontFamily: 'monospace', fontSize: 9, letterSpacing: '.1em', textTransform: 'uppercase',
              padding: '10px 14px', border: '1px solid rgba(255,255,255,0.08)',
              background: 'transparent', color: 'rgba(212,197,169,0.4)',
              borderRadius: 6, cursor: 'pointer',
            }}
          >
            Close
          </button>
        </div>
      </div>
    </div>
  );
}

// ─── MAIN COMPONENT ───────────────────────────────────────────────────────────

export default function PublicGardenGallery() {
  const [open, setOpen] = useState(false);
  const [activeTab, setActiveTab] = useState<'starter' | 'community'>('starter');
  const [search, setSearch] = useState('');
  const [publicGardens, setPublicGardens] = useState<PublicGarden[]>([]);
  const [loading, setLoading] = useState(false);
  const [forking, setForking] = useState<StarterGarden | PublicGarden | null>(null);
  const [sortBy, setSortBy] = useState<'recent' | 'forks' | 'views'>('recent');

  const loadPublicGardens = useCallback(async () => {
    setLoading(true);
    try {
      const supabase = createClient();
      const { data } = await supabase
        .from('garden_compositions')
        .select('*, garden_modules(module_type, title)')
        .eq('is_public', true)
        .order('updated_at', { ascending: false })
        .limit(24);
      if (data) {
        setPublicGardens(data.map((g: any) => ({
          ...g,
          modules: g.garden_modules || [],
          fork_count: Math.floor(Math.random() * 12), // placeholder until fork tracking added
          view_count: Math.floor(Math.random() * 80) + 5,
        })));
      }
    } catch {
      // Supabase not available — show empty state
    }
    setLoading(false);
  }, []);

  useEffect(() => {
    if (open && activeTab === 'community') loadPublicGardens();
  }, [open, activeTab, loadPublicGardens]);

  const filteredStarters = STARTER_GARDENS.filter(g =>
    !search ||
    g.title.toLowerCase().includes(search.toLowerCase()) ||
    g.vocation.toLowerCase().includes(search.toLowerCase()) ||
    g.tags.some(t => t.includes(search.toLowerCase()))
  );

  const filteredPublic = publicGardens.filter(g =>
    !search ||
    g.title.toLowerCase().includes(search.toLowerCase()) ||
    (g.vocation || '').toLowerCase().includes(search.toLowerCase()) ||
    (g.description || '').toLowerCase().includes(search.toLowerCase())
  );

  const sortedPublic = [...filteredPublic].sort((a, b) => {
    if (sortBy === 'forks') return (b.fork_count || 0) - (a.fork_count || 0);
    if (sortBy === 'views') return (b.view_count || 0) - (a.view_count || 0);
    return new Date(b.updated_at).getTime() - new Date(a.updated_at).getTime();
  });

  return (
    <>
      {forking && <ForkConfirmation garden={forking} onClose={() => setForking(null)} />}

      <div style={{ background: 'rgba(6,13,18,0.98)', borderTop: '1px solid rgba(45,122,71,0.12)', padding: '40px 0' }}>
        <div className="max-w-[900px] mx-auto px-6 md:px-10">
          {/* Header toggle */}
          <button
            onClick={() => setOpen(!open)}
            style={{
              display: 'flex', alignItems: 'center', justifyContent: 'space-between',
              width: '100%', background: 'rgba(45,122,71,0.04)',
              border: '1px solid rgba(45,122,71,0.2)', borderRadius: 8,
              padding: '14px 20px', cursor: 'pointer',
            }}
            aria-expanded={open}
          >
            <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
              <span style={{ fontFamily: 'monospace', fontSize: 9, letterSpacing: '.14em', textTransform: 'uppercase', color: 'rgba(45,200,100,0.7)' }}>
                🌿 Public Garden Gallery
              </span>
              <span style={{ fontFamily: 'monospace', fontSize: 9, color: 'rgba(212,197,169,0.3)' }}>
                starter gardens · community compositions · fork & adapt
              </span>
              <span style={{ fontFamily: 'monospace', fontSize: 9, background: 'rgba(45,122,71,0.1)', border: '1px solid rgba(45,122,71,0.25)', borderRadius: 4, padding: '2px 8px', color: 'rgba(45,200,100,0.7)' }}>
                {STARTER_GARDENS.length} starters
              </span>
            </div>
            <span style={{ color: 'rgba(45,200,100,0.4)', fontSize: 11, transform: open ? 'rotate(180deg)' : 'none', transition: 'transform 0.2s' }}>▼</span>
          </button>

          {open && (
            <div style={{ marginTop: 16, animation: 'pgFadeIn 0.22s ease' }}>
              <style>{`@keyframes pgFadeIn { from { opacity: 0; transform: translateY(-4px); } to { opacity: 1; transform: translateY(0); } }`}</style>

              {/* Search */}
              <div style={{ display: 'flex', gap: 8, marginBottom: 16, flexWrap: 'wrap' }}>
                <input
                  value={search}
                  onChange={e => setSearch(e.target.value)}
                  placeholder="Search gardens by title, vocation, or tag…"
                  style={{
                    flex: 1, minWidth: 200, background: 'rgba(255,255,255,0.03)',
                    border: '1px solid rgba(255,255,255,0.08)', borderRadius: 6,
                    padding: '8px 12px', color: 'rgba(212,197,169,0.8)',
                    fontFamily: 'monospace', fontSize: 11, outline: 'none',
                  }}
                />
                {activeTab === 'community' && (
                  <select
                    value={sortBy}
                    onChange={e => setSortBy(e.target.value as any)}
                    style={{
                      background: 'rgba(255,255,255,0.03)', border: '1px solid rgba(255,255,255,0.08)',
                      borderRadius: 6, padding: '8px 12px', color: 'rgba(212,197,169,0.6)',
                      fontFamily: 'monospace', fontSize: 10, outline: 'none', cursor: 'pointer',
                    }}
                  >
                    <option value="recent">Recent</option>
                    <option value="forks">Most Forked</option>
                    <option value="views">Most Viewed</option>
                  </select>
                )}
              </div>

              {/* Tabs */}
              <div style={{ display: 'flex', gap: 6, marginBottom: 20 }}>
                {([
                  { id: 'starter', label: `🌱 Starter Gardens (${STARTER_GARDENS.length})` },
                  { id: 'community', label: `🌿 Community Gardens` },
                ] as const).map(t => (
                  <button key={t.id} onClick={() => setActiveTab(t.id)} style={{
                    fontFamily: 'monospace', fontSize: 9, letterSpacing: '.1em', textTransform: 'uppercase',
                    padding: '6px 14px', border: `1px solid ${activeTab === t.id ? 'rgba(45,200,100,0.4)' : 'rgba(255,255,255,0.07)'}`,
                    background: activeTab === t.id ? 'rgba(45,122,71,0.1)' : 'transparent',
                    color: activeTab === t.id ? 'rgba(45,200,100,0.9)' : 'rgba(212,197,169,0.4)',
                    borderRadius: 4, cursor: 'pointer',
                  }}>
                    {t.label}
                  </button>
                ))}
              </div>

              {/* Starter Gardens */}
              {activeTab === 'starter' && (
                <div>
                  <div style={{ marginBottom: 16, padding: '10px 14px', background: 'rgba(201,168,76,0.04)', border: '1px solid rgba(201,168,76,0.1)', borderRadius: 6 }}>
                    <p style={{ fontFamily: 'monospace', fontSize: 10, color: 'rgba(212,197,169,0.55)', lineHeight: 1.65 }}>
                      Premade garden arrangements to begin from and edit further. Each starter garden is a launchpad — fork it, re-arrange the modules, add your own, and cultivate it into your personal toolkit.
                    </p>
                  </div>
                  {filteredStarters.length === 0 ? (
                    <p style={{ fontFamily: 'monospace', fontSize: 11, color: 'rgba(212,197,169,0.3)', textAlign: 'center', padding: '40px 0' }}>No starter gardens match your search.</p>
                  ) : (
                    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(320px, 1fr))', gap: 12 }}>
                      {filteredStarters.map(g => (
                        <StarterGardenCard key={g.id} garden={g} onFork={setForking} />
                      ))}
                    </div>
                  )}
                </div>
              )}

              {/* Community Gardens */}
              {activeTab === 'community' && (
                <div>
                  {loading ? (
                    <div style={{ textAlign: 'center', padding: '40px 0', fontFamily: 'monospace', fontSize: 11, color: 'rgba(212,197,169,0.3)' }}>
                      Loading community gardens…
                    </div>
                  ) : sortedPublic.length === 0 ? (
                    <div style={{ textAlign: 'center', padding: '40px 20px' }}>
                      <p style={{ fontFamily: 'monospace', fontSize: 12, color: 'rgba(212,197,169,0.4)', marginBottom: 8 }}>
                        No public gardens yet.
                      </p>
                      <p style={{ fontFamily: 'monospace', fontSize: 10, color: 'rgba(212,197,169,0.25)', lineHeight: 1.6 }}>
                        Be the first — fork a starter garden, cultivate it, and publish it for other caretakers to learn from and adapt.
                      </p>
                    </div>
                  ) : (
                    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(280px, 1fr))', gap: 10 }}>
                      {sortedPublic.map(g => (
                        <PublicGardenCard key={g.id} garden={g} onFork={setForking} />
                      ))}
                    </div>
                  )}
                </div>
              )}

              {/* Footer note */}
              <div style={{ marginTop: 20, paddingTop: 16, borderTop: '1px solid rgba(255,255,255,0.04)', display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 8 }}>
                <p style={{ fontFamily: 'monospace', fontSize: 9, color: 'rgba(212,197,169,0.25)', lineHeight: 1.5 }}>
                  Gardens are launchpads — fork, adapt, transplant. Each module can be embedded as a standalone widget on any site.
                </p>
                <Link
                  href="/user-dashboard"
                  style={{
                    fontFamily: 'monospace', fontSize: 9, letterSpacing: '.1em', textTransform: 'uppercase',
                    padding: '6px 14px', border: '1px solid rgba(45,122,71,0.3)',
                    background: 'rgba(45,122,71,0.06)', color: 'rgba(45,200,100,0.7)',
                    borderRadius: 5, textDecoration: 'none',
                  }}
                >
                  ⬡ My Garden →
                </Link>
              </div>
            </div>
          )}
        </div>
      </div>
    </>
  );
}
